// components/estimates/EstimateAddItemModal.js
// Modal per aggiungere voci al preventivo

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Search, Plus, Check } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { parseItalianNumber, normalizeNumericInput } from '../../lib/numericUtils';
import FormulaInput from '../FormulaInput';

const CATEGORIES = [
  { code: 'A', label: 'Progettazione' },
  { code: 'B', label: 'Pratiche' },
  { code: 'C', label: 'Opere edili' },
  { code: 'D', label: 'Impianti elettrici' },
  { code: 'E', label: 'Impianti idraulici, termici e climatizzazione' },
  { code: 'F', label: 'Finiture' },
  { code: 'G', label: 'Serramenti' },
  { code: 'H', label: 'Prodotti' },
  { code: 'S', label: 'Sicurezza' },
];

const EstimateAddItemModal = ({
  open,
  onOpenChange,
  activeCategory,
  activeSubcategories,
  selectedSubcategory,
  setSelectedSubcategory,
  searchTerm,
  setSearchTerm,
  newItem,
  setNewItem,
  filteredPriceList,
  isItemInEstimate,
  onAddItemFromPriceList,
  onAddManualItem
}) => {
  const currentCategoryLabel = CATEGORIES.find(c => c.code === activeCategory)?.label;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Aggiungi voce · {activeCategory} {currentCategoryLabel}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Subcategory filter */}
          {activeSubcategories.length > 0 && (
            <div>
              <Label>Sottocategoria</Label>
              <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                <SelectTrigger data-testid="subcategory-select">
                  <SelectValue placeholder="Tutte le sottocategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le sottocategorie</SelectItem>
                  {activeSubcategories.map(sub => (
                    <SelectItem key={sub.code} value={sub.code}>
                      {sub.code} - {sub.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#737373] w-4 h-4" />
            <Input
              placeholder="Cerca nel listino..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="price-list-search"
            />
          </div>
          
          {/* Quantity input */}
          <div className="flex items-center gap-4">
            <Label>Quantità da aggiungere:</Label>
            <FormulaInput
              value={newItem.quantity}
              formula={newItem.quantity_formula || ''}
              onChange={(val, formula) => setNewItem(prev => ({ ...prev, quantity: val, quantity_formula: formula }))}
              className="w-32"
              placeholder="es: 2,5*3"
            />
          </div>
          
          {/* Price list */}
          <div className="border rounded-lg max-h-[300px] overflow-y-auto">
            {filteredPriceList.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-[#F2F2F2] sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3">Codice</th>
                    <th className="text-left py-2 px-3">Descrizione</th>
                    <th className="text-center py-2 px-3">U.M.</th>
                    <th className="text-right py-2 px-3">Prezzo</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPriceList.map(item => {
                    const alreadyAdded = isItemInEstimate(item.id);
                    return (
                      <tr 
                        key={item.id} 
                        className={`border-b ${alreadyAdded ? 'bg-green-50 opacity-60' : 'hover:bg-[#FAFAFA]'}`}
                        data-testid={`price-list-item-${item.id}`}
                      >
                        <td className="py-2 px-3 font-medium">{item.code}</td>
                        <td className="py-2 px-3">
                          <div>
                            {item.description}
                            {alreadyAdded && (
                              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                Già inserito
                              </span>
                            )}
                            {item.notes && (
                              <p className="text-xs text-[#FF7819] mt-1 italic flex items-start gap-1">
                                <span className="font-semibold">⚠️ Nota:</span> {item.notes}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-center">{item.unit}</td>
                        <td className="py-2 px-3 text-right">{formatCurrency(item.unit_price)}</td>
                        <td className="py-2 px-3">
                          {alreadyAdded ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              disabled
                              className="opacity-50 cursor-not-allowed"
                            >
                              <Check className="w-4 h-4 text-green-600" />
                            </Button>
                          ) : (
                            <Button 
                              size="sm" 
                              onClick={() => onAddItemFromPriceList(item)}
                              className="btn-primary"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-[#737373]" data-testid="empty-price-list">
                Nessuna voce trovata nel listino
              </div>
            )}
          </div>
          
          {/* Manual entry separator */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-[#DCDCDC]" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-[#737373]">Oppure inserisci manualmente</span>
            </div>
          </div>
          
          {/* Manual entry form */}
          <ManualEntryForm
            newItem={newItem}
            setNewItem={setNewItem}
          />
        </div>
        
        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="ghost" onClick={onAddManualItem} className="text-[#737373] hover:text-[#FF7819]" data-testid="add-manual-btn">
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi manualmente
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Manual entry form sub-component
const ManualEntryForm = ({ newItem, setNewItem }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
    <div>
      <Label>Codice</Label>
      <Input
        value={newItem.code}
        onChange={(e) => setNewItem(prev => ({ ...prev, code: e.target.value }))}
        placeholder="Es. MAN-001"
        data-testid="manual-code-input"
      />
    </div>
    <div className="col-span-2">
      <Label>Descrizione *</Label>
      <Input
        value={newItem.description}
        onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
        placeholder="Descrizione voce"
        data-testid="manual-description-input"
      />
    </div>
    <div>
      <Label>U.M.</Label>
      <Select value={newItem.unit} onValueChange={(v) => setNewItem(prev => ({ ...prev, unit: v }))}>
        <SelectTrigger data-testid="manual-unit-select">
          <SelectValue placeholder="Seleziona" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="mq">mq</SelectItem>
          <SelectItem value="ml">ml</SelectItem>
          <SelectItem value="nr">nr</SelectItem>
          <SelectItem value="mc">mc</SelectItem>
          <SelectItem value="kg">kg</SelectItem>
          <SelectItem value="a corpo">a corpo</SelectItem>
          <SelectItem value="cad">cad</SelectItem>
          <SelectItem value="h">h</SelectItem>
          <SelectItem value="giorno">giorno</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div>
      <Label>Quantità</Label>
      <FormulaInput
        value={newItem.quantity}
        formula={newItem.quantity_formula || ''}
        onChange={(val, formula) => setNewItem(prev => ({ ...prev, quantity: val, quantity_formula: formula }))}
        placeholder="es: 2,5*3+1"
      />
    </div>
    <div>
      <Label>Prezzo Unit. *</Label>
      <Input
        type="text"
        inputMode="decimal"
        value={newItem.unit_price}
        onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: normalizeNumericInput(e.target.value) }))}
        onBlur={(e) => {
          const parsed = parseItalianNumber(e.target.value, 0);
          setNewItem(prev => ({ ...prev, unit_price: Math.max(0, parsed) }));
        }}
        data-testid="manual-price-input"
      />
    </div>
    <div>
      <Label>Totale</Label>
      <div className="h-10 flex items-center font-medium text-[#FF7819]">
        {formatCurrency((parseItalianNumber(newItem.quantity, 0) || 0) * (parseItalianNumber(newItem.unit_price, 0) || 0))}
      </div>
    </div>
  </div>
);

export default EstimateAddItemModal;
